import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, FormControlLabel, Paper, TextField, MenuItem, Typography } from '@material-ui/core';
import api from '../../../services/api';

const vazio = { desccreditodebito: '', tipolancamento: 'C', tipoconta: 'A', inativo: 'N', codcreddebpai: null };
const id = item => Number(item.codcreditodebito);
const pai = item => Number(item.codcreddebpai);
const erro = e => e.response?.data?.error || e.response?.data?.mensagem || e.message || 'Erro inesperado';

export default function CentroReceitaDespesa() {
  const [itens, setItens] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [form, setForm] = useState(vazio);
  const [editando, setEditando] = useState(false);
  const [novo, setNovo] = useState(false);
  const [inativos, setInativos] = useState(false);
  const [busca, setBusca] = useState('');
  const [abertos, setAbertos] = useState({});
  const [mensagem, setMensagem] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const carregar = async () => {
    try {
      const resposta = await api.get('/financas/centroreceitadespesa?tree=1');
      setItens(Array.isArray(resposta.data) ? resposta.data : []);
    } catch (e) { setMensagem(erro(e)); }
  };
  useEffect(() => { carregar(); }, []);
  const selecionar = item => { if (editando) return; setSelecionado(id(item)); setForm({ ...item }); setNovo(false); setMensagem(''); };
  const mudar = (campo, valor) => setForm(atual => ({ ...atual, [campo]: valor }));
  const criar = () => {
    const atual = itens.find(x => id(x) === selecionado);
    const podeFilho = atual && atual.tipoconta === 'S';
    setForm({ ...vazio, tipolancamento: atual?.tipolancamento || 'C', codcreddebpai: podeFilho ? id(atual) : null });
    setNovo(true); setEditando(true); setMensagem('');
  };
  const cancelar = () => { setEditando(false); setNovo(false); setForm(itens.find(x => id(x) === selecionado) || vazio); };
  const salvar = async () => {
    if (!form.desccreditodebito?.trim()) return setMensagem('Informe a descrição.');
    const paiItem = itens.find(x => id(x) === Number(form.codcreddebpai));
    if (novo && paiItem && (paiItem.tipoconta !== 'S' || paiItem.tipolancamento !== form.tipolancamento)) return setMensagem('O centro pai deve ser sintético e do mesmo tipo de lançamento.');
    setOcupado(true); setMensagem('');
    try {
      const dados = { ...form, desccreditodebito: form.desccreditodebito.trim().toUpperCase(), codusuarioregistro: Number(sessionStorage.getItem('codUser') || 0) };
      if (novo) await api.post('/financas/centroreceitadespesa', dados);
      else await api.post(`/financas/centroreceitadespesa/${selecionado}`, dados);
      await carregar(); setEditando(false); setNovo(false); setMensagem('Registro salvo com sucesso.');
    } catch (e) { setMensagem(erro(e)); } finally { setOcupado(false); }
  };
  const excluir = async () => {
    if (!selecionado || !window.confirm('Excluir este centro? A exclusão será bloqueada caso existam filhos ou lançamentos vinculados.')) return;
    setOcupado(true);
    try { await api.delete(`/financas/centroreceitadespesa/${selecionado}`); setSelecionado(null); setForm(vazio); await carregar(); setMensagem('Centro excluído.'); }
    catch (e) { setMensagem(erro(e)); } finally { setOcupado(false); }
  };
  const visiveis = useMemo(() => itens.filter(x => inativos || x.inativo !== 'S'), [itens, inativos]);
  const porPai = useMemo(() => {
    const mapa = {};
    visiveis.forEach(x => { const chave = pai(x) === id(x) || !visiveis.some(y => id(y) === pai(x)) ? 'raiz' : String(pai(x)); (mapa[chave] ||= []).push(x); });
    Object.values(mapa).forEach(lista => lista.sort((a,b) => String(a.classificacao || '').localeCompare(String(b.classificacao || ''), undefined, {numeric:true})));
    return mapa;
  }, [visiveis]);
  const corresponde = x => !busca || [x.desccreditodebito, x.classificacao, x.codcreditodebito].some(v => String(v || '').toLocaleLowerCase().includes(busca.toLocaleLowerCase()));
  const renderizar = (chave, nivel = 0, visitados = new Set()) => (porPai[chave] || []).map(x => {
    const codigo = id(x); if (visitados.has(codigo)) return null;
    const filhos = porPai[String(codigo)] || [];
    const expandido = busca ? true : abertos[codigo] !== false;
    const caminho = new Set(visitados); caminho.add(codigo);
    const mostrar = !busca || corresponde(x) || filhos.some(f => corresponde(f));
    if (!mostrar) return null;
    return <React.Fragment key={codigo}><div style={{paddingLeft: nivel * 17, display:'flex', alignItems:'center', background: selecionado === codigo ? '#dcecfb' : 'transparent', borderBottom:'1px solid #eee'}}>
      <button type="button" style={{border:0, background:'transparent', cursor:'pointer', width:24}} onClick={() => setAbertos(a => ({...a,[codigo]:!expandido}))}>{filhos.length ? (expandido ? '▾' : '▸') : '·'}</button>
      <button type="button" onClick={() => selecionar(x)} style={{flex:1, textAlign:'left', padding:'8px 2px', background:'transparent', border:0, cursor:'pointer', color:x.inativo === 'S' ? '#999' : 'inherit'}}>{x.desccreditodebito} [{x.classificacao || codigo}] {x.inativo === 'S' ? '(Inativo)' : ''}</button>
    </div>{expandido && renderizar(String(codigo), nivel+1, caminho)}</React.Fragment>;
  });
  return <div style={{padding:24}}><Typography variant="h5" gutterBottom>Centros de Receitas e Despesas</Typography>
    <Paper style={{display:'flex', flexWrap:'wrap', minHeight:510}}>
      <div style={{flex:'1 1 350px', padding:16, borderRight:'1px solid #ddd', maxHeight:650, overflowY:'auto'}}>
        <TextField label="Localizar centro (código, descrição ou classificação)" fullWidth value={busca} onChange={e=>setBusca(e.target.value)} margin="dense" />
        <FormControlLabel control={<Checkbox checked={inativos} onChange={e=>setInativos(e.target.checked)}/>} label="Mostrar inativos" />
        <div>{renderizar('raiz')}</div>
      </div>
      <div style={{flex:'1 1 350px', padding:20, display:'flex', flexDirection:'column', gap:14}}>
        <TextField label="Código" value={novo ? 'Novo' : form.codcreditodebito || ''} disabled />
        <TextField label="Centro de Receita e Despesa" value={form.desccreditodebito || ''} onChange={e=>mudar('desccreditodebito',e.target.value.toUpperCase())} disabled={!editando} fullWidth required />
        <TextField select label="Tipo Lançamento" value={form.tipolancamento || 'C'} onChange={e=>mudar('tipolancamento',e.target.value)} disabled={!editando}><MenuItem value="C">C - Crédito</MenuItem><MenuItem value="D">D - Débito</MenuItem></TextField>
        <TextField select label="Tipo da Conta" value={form.tipoconta || 'A'} onChange={e=>mudar('tipoconta',e.target.value)} disabled={!editando}><MenuItem value="A">Analítica</MenuItem><MenuItem value="S">Sintética</MenuItem></TextField>
        <TextField label="Classificação" value={form.classificacao || '(automática)'} disabled />
        <TextField label="Centro pai" value={novo ? (form.codcreddebpai || 'Raiz') : (form.codcreddebpai || '')} disabled />
        <FormControlLabel control={<Checkbox checked={form.inativo === 'S'} onChange={e=>mudar('inativo',e.target.checked ? 'S':'N')} disabled={!editando}/>} label="Inativo" />
      </div>
    </Paper>
    {mensagem && <Typography style={{marginTop:12}} color="secondary">{mensagem}</Typography>}
    <div style={{display:'flex', gap:10, marginTop:16, flexWrap:'wrap'}}>
      <Button variant="contained" color="primary" onClick={criar} disabled={editando || ocupado}>Cadastrar</Button>
      {!novo && <Button onClick={()=>setEditando(true)} disabled={!selecionado || editando || ocupado}>Alterar</Button>}
      <Button variant="contained" color="primary" onClick={salvar} disabled={!editando || ocupado}>Salvar</Button>
      <Button onClick={cancelar} disabled={!editando || ocupado}>Cancelar</Button>
      <Button onClick={excluir} disabled={!selecionado || editando || ocupado}>Excluir</Button>
      <Button onClick={()=>window.print()}>Imprimir</Button>
      <Button onClick={carregar} disabled={ocupado}>Atualizar</Button>
    </div>
  </div>;
}
